import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  HairlineCard,
  Icons,
  iconSize,
  MenuItem,
  Stack,
  StatusChip,
  TextField,
  Typography,
  useConfirm,
} from '@munifocus/ui';
import {
  calendarDateIn,
  type ReinspectionBookingDto,
  type ReinspectionOutcome,
  type ReinspectionStatus,
} from '@munifocus/contracts-gov';
import { useMutationToast } from '../../lib/mutation-toast';
import {
  addDays,
  formatDay,
  formatSlot,
  formatTime,
  groupSlotsByDay,
  useBookReinspection,
  useCancelReinspection,
  useFileReinspection,
  useOpenReinspection,
  useRecordReinspectionOutcome,
  useReinspectionSlots,
  useWithdrawReinspection,
} from '../../lib/inspection-booking-api';

const STATUS_KIND: Record<ReinspectionStatus, 'pending' | 'success' | 'warning' | 'neutral'> = {
  awaiting_booking: 'pending',
  booked: 'success',
  completed: 'success',
  no_show: 'warning',
  withdrawn: 'neutral',
};

function OpenDialog({
  open,
  saving,
  onClose,
  onOpen,
}: {
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onOpen: (earliestDate: string, latestDate: string) => void;
}) {
  const { t } = useTranslation();
  const today = calendarDateIn(new Date());
  const [earliest, setEarliest] = useState(today);
  const [latest, setLatest] = useState(addDays(today, 14));
  const valid = !!earliest && !!latest && earliest <= latest && latest >= today;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('gov.inspectionBooking.panel.openTitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('gov.inspectionBooking.panel.openBody')}
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            type="date"
            label={t('gov.inspectionBooking.panel.earliest')}
            value={earliest}
            onChange={(e) => setEarliest(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            label={t('gov.inspectionBooking.panel.latest')}
            value={latest}
            onChange={(e) => setLatest(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {!valid && (
            <Alert severity="warning">{t('gov.inspectionBooking.panel.invalidDates')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || saving}
          onClick={() => onOpen(earliest, latest)}
        >
          {t('gov.inspectionBooking.panel.open')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SlotDialog({
  fileId,
  open,
  saving,
  onClose,
  onBook,
}: {
  fileId: string;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onBook: (startsAt: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const slots = useReinspectionSlots(fileId, open);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('gov.inspectionBooking.panel.pickTitle')}</DialogTitle>
      <DialogContent>
        {slots.isLoading && (
          <CircularProgress size={20} aria-label={t('gov.inspectionBooking.panel.slotsLoading')} />
        )}
        {slots.isError && (
          <Alert severity="error">{t('gov.inspectionBooking.panel.loadError')}</Alert>
        )}
        {slots.data && slots.data.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('gov.inspectionBooking.panel.noSlots')}
          </Typography>
        )}
        <Stack spacing={2}>
          {groupSlotsByDay(slots.data ?? []).map(([day, list]) => (
            <Box key={day}>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                {formatDay(day, i18n.language)}
              </Typography>
              <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }} spacing={1}>
                {list.map((s) => (
                  <Button
                    key={s.startsAt}
                    variant="outlined"
                    size="small"
                    disabled={saving}
                    aria-label={t('gov.inspectionBooking.panel.bookSlot', {
                      when: formatSlot(s.startsAt, i18n.language),
                    })}
                    onClick={() => onBook(s.startsAt)}
                  >
                    {formatTime(s.startsAt, i18n.language)}
                  </Button>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function OutcomeDialog({
  booking,
  open,
  saving,
  onClose,
  onSave,
}: {
  booking: ReinspectionBookingDto;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (outcome: ReinspectionOutcome, note: string) => void;
}) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<ReinspectionOutcome>('completed');
  const [note, setNote] = useState('');
  const started = !!booking.startsAt && new Date(booking.startsAt).getTime() <= Date.now();
  const blocked = outcome === 'no_show' && !started;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('gov.inspectionBooking.panel.outcomeTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label={t('gov.inspectionBooking.panel.outcome')}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as ReinspectionOutcome)}
          >
            <MenuItem value="completed">
              {t('gov.inspectionBooking.panel.outcomes.completed')}
            </MenuItem>
            <MenuItem value="no_show">{t('gov.inspectionBooking.panel.outcomes.no_show')}</MenuItem>
          </TextField>
          {blocked && (
            <Alert severity="info">{t('gov.inspectionBooking.panel.noShowTooEarly')}</Alert>
          )}
          <TextField
            label={t('gov.inspectionBooking.panel.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={saving || blocked}
          onClick={() => onSave(outcome, note.trim())}
        >
          {t('gov.inspectionBooking.panel.saveOutcome')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The re-inspection on a file: open it for booking with the dates a slot may
 * fall between, book or move a slot on the owner's behalf, cancel, close it
 * unbooked, and record the outcome. Renders nothing where the municipality has
 * booking off. Times are Moncton wall-clock.
 */
export function ReinspectionPanel({ fileId, canEdit }: { fileId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const confirm = useConfirm();
  const { data, isLoading, isError } = useFileReinspection(fileId);
  const openBooking = useOpenReinspection(fileId);
  const book = useBookReinspection(fileId);
  const cancel = useCancelReinspection(fileId);
  const withdraw = useWithdrawReinspection(fileId);
  const outcome = useRecordReinspectionOutcome(fileId);
  const [opening, setOpening] = useState(false);
  const [picking, setPicking] = useState(false);
  const [recording, setRecording] = useState(false);

  if (data && !data.enabled) return null;
  const booking = data?.booking ?? null;
  const fail = (err: unknown) => toast.error(err, 'gov.inspectionBooking.panel.failed');

  const onCancel = async () => {
    const ok = await confirm({
      title: t('gov.inspectionBooking.panel.cancelTitle'),
      body: t('gov.inspectionBooking.panel.cancelBody'),
      confirmLabel: t('gov.inspectionBooking.panel.cancel'),
      cancelLabel: t('gov.inspectionBooking.panel.keep'),
      tone: 'danger',
    });
    if (!ok) return;
    cancel.mutate(undefined, {
      onSuccess: () => toast.success('gov.inspectionBooking.panel.cancelled'),
      onError: fail,
    });
  };

  const onWithdraw = () =>
    withdraw.mutate(undefined, {
      onSuccess: () => toast.success('gov.inspectionBooking.panel.withdrawn'),
      onError: fail,
    });

  return (
    <HairlineCard component="section" aria-labelledby="reinspection-title" sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Icons.CalendarClock size={iconSize.md} aria-hidden />
          <Typography id="reinspection-title" variant="overline" component="h2">
            {t('gov.inspectionBooking.panel.title')}
          </Typography>
        </Stack>
        {canEdit && data && !booking && (
          <Button
            size="small"
            startIcon={<Icons.Plus size={iconSize.sm} aria-hidden />}
            onClick={() => setOpening(true)}
          >
            {t('gov.inspectionBooking.panel.open')}
          </Button>
        )}
      </Stack>

      {isLoading && <CircularProgress size={20} />}
      {isError && <Alert severity="error">{t('gov.inspectionBooking.panel.loadError')}</Alert>}
      {data && !booking && (
        <Typography variant="body2" color="text.secondary">
          {t('gov.inspectionBooking.panel.none')}
        </Typography>
      )}

      {booking && (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusChip
              status={STATUS_KIND[booking.status]}
              label={t(`gov.inspectionBooking.panel.status.${booking.status}`)}
            />
            <Typography variant="body2" color="text.secondary">
              {t('gov.inspectionBooking.panel.window', {
                from: formatDay(booking.earliestDate, i18n.language),
                to: formatDay(booking.latestDate, i18n.language),
              })}
            </Typography>
          </Stack>
          {booking.status === 'booked' && booking.startsAt && (
            <Typography sx={{ fontWeight: 700 }}>
              {t('gov.inspectionBooking.panel.bookedFor', {
                when: formatSlot(booking.startsAt, i18n.language),
              })}
            </Typography>
          )}
          {booking.officerName && (
            <Typography variant="body2">
              {t('gov.inspectionBooking.panel.inspector', { name: booking.officerName })}
            </Typography>
          )}
          {booking.status === 'booked' && booking.bookedBy && (
            <Typography variant="caption" color="text.secondary">
              {booking.bookedBy === 'owner'
                ? t('gov.inspectionBooking.panel.bookedByOwner')
                : t('gov.inspectionBooking.panel.bookedByOfficer')}
              {booking.rescheduleCount > 0
                ? ` · ${t('gov.inspectionBooking.panel.rescheduled', { count: booking.rescheduleCount })}`
                : ''}
            </Typography>
          )}
          {canEdit && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              <Button size="small" variant="contained" onClick={() => setPicking(true)}>
                {booking.status === 'booked'
                  ? t('gov.inspectionBooking.panel.reschedule')
                  : t('gov.inspectionBooking.panel.bookForOwner')}
              </Button>
              {booking.status === 'booked' && (
                <>
                  <Button size="small" onClick={() => setRecording(true)}>
                    {t('gov.inspectionBooking.panel.recordOutcome')}
                  </Button>
                  <Button size="small" color="error" onClick={() => void onCancel()}>
                    {t('gov.inspectionBooking.panel.cancel')}
                  </Button>
                </>
              )}
              {booking.status === 'awaiting_booking' && (
                <Button size="small" disabled={withdraw.isPending} onClick={onWithdraw}>
                  {t('gov.inspectionBooking.panel.withdraw')}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      )}

      {data && data.history.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" component="h3">
            {t('gov.inspectionBooking.panel.history')}
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {data.history.map((h) => (
              <Typography component="li" variant="body2" key={h.id}>
                {t('gov.inspectionBooking.panel.historyLine', {
                  status: t(`gov.inspectionBooking.panel.status.${h.status}`),
                  when: h.startsAt
                    ? formatSlot(h.startsAt, i18n.language)
                    : formatDay(h.latestDate, i18n.language),
                })}
                {h.outcomeNote ? ` · ${h.outcomeNote}` : ''}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {opening && (
        <OpenDialog
          open
          saving={openBooking.isPending}
          onClose={() => setOpening(false)}
          onOpen={(earliestDate, latestDate) =>
            openBooking.mutate(
              { earliestDate, latestDate },
              {
                onSuccess: () => {
                  toast.success('gov.inspectionBooking.panel.opened');
                  setOpening(false);
                },
                onError: fail,
              },
            )
          }
        />
      )}
      {picking && (
        <SlotDialog
          fileId={fileId}
          open
          saving={book.isPending}
          onClose={() => setPicking(false)}
          onBook={(startsAt) =>
            book.mutate(startsAt, {
              onSuccess: () => {
                toast.success('gov.inspectionBooking.panel.booked');
                setPicking(false);
              },
              onError: fail,
            })
          }
        />
      )}
      {recording && booking && (
        <OutcomeDialog
          booking={booking}
          open
          saving={outcome.isPending}
          onClose={() => setRecording(false)}
          onSave={(result, note) =>
            outcome.mutate(
              { outcome: result, ...(note ? { note } : {}) },
              {
                onSuccess: () => {
                  toast.success('gov.inspectionBooking.panel.outcomeSaved');
                  setRecording(false);
                },
                onError: fail,
              },
            )
          }
        />
      )}
    </HairlineCard>
  );
}
